import React, { useState, useEffect } from 'react';
import { XMarkIcon, SparklesIcon, DocumentTextIcon, BookOpenIcon } from '@heroicons/react/24/outline';

export interface StyleConfig {
  heading1: {
    fontFamily: string;
    fontSize: number;
    color: string;
    lineHeight: number;
    pageBreak?: boolean; // 章节分页
  };
  heading2: {
    fontFamily: string;
    fontSize: number;
    color: string;
    lineHeight: number;
    pageBreak?: boolean; // 章节分页
  };
  heading3: {
    fontFamily: string;
    fontSize: number;
    color: string;
    lineHeight: number;
    pageBreak?: boolean; // 章节分页
  };
  heading4?: {
    fontFamily: string;
    fontSize: number;
    color: string;
    lineHeight: number;
    pageBreak?: boolean; // 章节分页
  };
  heading5?: {
    fontFamily: string;
    fontSize: number;
    color: string;
    lineHeight: number;
    pageBreak?: boolean; // 章节分页
  };
  heading6?: {
    fontFamily: string;
    fontSize: number;
    color: string;
    lineHeight: number;
    pageBreak?: boolean; // 章节分页
  };
  body: {
    fontFamily: string;
    fontSize: number;
    color: string;
    lineHeight: number;
  };
  paragraphIndent?: boolean; // 段落开头缩进（默认true）
}

export const DEFAULT_STYLE_CONFIG: StyleConfig = {
  heading1: {
    fontFamily: '黑体',
    fontSize: 22,
    color: '#000000',
    lineHeight: 1.5,
    pageBreak: false,
  },
  heading2: {
    fontFamily: '黑体',
    fontSize: 18,
    color: '#1a1a1a',
    lineHeight: 1.5,
    pageBreak: false,
  },
  heading3: {
    fontFamily: '黑体',
    fontSize: 16,
    color: '#333333',
    lineHeight: 1.5,
    pageBreak: false,
  },
  heading4: {
    fontFamily: '黑体',
    fontSize: 14,
    color: '#444444',
    lineHeight: 1.5,
    pageBreak: false,
  },
  heading5: {
    fontFamily: '黑体',
    fontSize: 14,
    color: '#555555',
    lineHeight: 1.5,
    pageBreak: false,
  },
  heading6: {
    fontFamily: '黑体',
    fontSize: 14,
    color: '#666666',
    lineHeight: 1.5,
    pageBreak: false,
  },
  body: {
    fontFamily: '宋体',
    fontSize: 14,
    color: '#333333',
    lineHeight: 1.8,
  },
  paragraphIndent: true,
};

const PRESET_STYLES = {
  classic: {
    name: '经典商务',
    icon: DocumentTextIcon,
    config: DEFAULT_STYLE_CONFIG,
  },
  modern: {
    name: '现代简约',
    icon: SparklesIcon,
    config: {
      heading1: { fontFamily: '微软雅黑', fontSize: 24, color: '#1a1a1a', lineHeight: 1.4, pageBreak: false },
      heading2: { fontFamily: '微软雅黑', fontSize: 20, color: '#2c2c2c', lineHeight: 1.4, pageBreak: false },
      heading3: { fontFamily: '微软雅黑', fontSize: 16, color: '#404040', lineHeight: 1.4, pageBreak: false },
      heading4: { fontFamily: '微软雅黑', fontSize: 14, color: '#505050', lineHeight: 1.4, pageBreak: false },
      heading5: { fontFamily: '微软雅黑', fontSize: 14, color: '#606060', lineHeight: 1.4, pageBreak: false },
      heading6: { fontFamily: '微软雅黑', fontSize: 14, color: '#707070', lineHeight: 1.4, pageBreak: false },
      body: { fontFamily: '微软雅黑', fontSize: 14, color: '#4a4a4a', lineHeight: 1.8 },
      paragraphIndent: true,
    } as StyleConfig,
  },
  professional: {
    name: '专业正式',
    icon: BookOpenIcon,
    config: {
      heading1: { fontFamily: '方正小标宋', fontSize: 22, color: '#000000', lineHeight: 1.5, pageBreak: false },
      heading2: { fontFamily: '黑体', fontSize: 18, color: '#1a1a1a', lineHeight: 1.5, pageBreak: false },
      heading3: { fontFamily: '楷体', fontSize: 16, color: '#2c2c2c', lineHeight: 1.6, pageBreak: false },
      heading4: { fontFamily: '楷体', fontSize: 14, color: '#3c3c3c', lineHeight: 1.6, pageBreak: false },
      heading5: { fontFamily: '楷体', fontSize: 14, color: '#4c4c4c', lineHeight: 1.6, pageBreak: false },
      heading6: { fontFamily: '楷体', fontSize: 14, color: '#5c5c5c', lineHeight: 1.6, pageBreak: false },
      body: { fontFamily: '仿宋', fontSize: 14, color: '#333333', lineHeight: 2.0 },
      paragraphIndent: true,
    } as StyleConfig,
  },
};

export interface GlobalConfig {
  aiDirection: string; // 全局AI生成方向
}

export const DEFAULT_GLOBAL_CONFIG: GlobalConfig = {
  aiDirection: '',
};

interface StyleSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: StyleConfig, globalConfig?: GlobalConfig) => void;
  initialConfig?: StyleConfig;
  initialGlobalConfig?: GlobalConfig;
  maxHeadingLevel?: number; // 目录最大层级（1-6），根据实际目录动态显示
}

const FONT_OPTIONS = [
  { value: '宋体', label: '宋体' },
  { value: '黑体', label: '黑体' },
  { value: '楷体', label: '楷体' },
  { value: '仿宋', label: '仿宋' },
  { value: '微软雅黑', label: '微软雅黑' },
  { value: '方正小标宋', label: '方正小标宋' },
];

const FONT_SIZE_OPTIONS = [
  { value: 10, label: '10pt (五号)' },
  { value: 12, label: '12pt (小四)' },
  { value: 14, label: '14pt (四号)' },
  { value: 16, label: '16pt (小三)' },
  { value: 18, label: '18pt (三号)' },
  { value: 20, label: '20pt (小二)' },
  { value: 22, label: '22pt (二号)' },
  { value: 24, label: '24pt (小一)' },
  { value: 26, label: '26pt (一号)' },
  { value: 28, label: '28pt' },
  { value: 32, label: '32pt' },
  { value: 36, label: '36pt' },
];

const LINE_HEIGHT_OPTIONS = [
  { value: 1.0, label: '1.0 (单倍)' },
  { value: 1.15, label: '1.15' },
  { value: 1.5, label: '1.5 (1.5倍)' },
  { value: 1.8, label: '1.8' },
  { value: 2.0, label: '2.0 (双倍)' },
  { value: 2.5, label: '2.5' },
  { value: 3.0, label: '3.0' },
];

// 样式配置中可作为tab选中的key类型
type StyleTabKey = 'heading1' | 'heading2' | 'heading3' | 'heading4' | 'heading5' | 'heading6' | 'body';

export const StyleSettingsDialog: React.FC<StyleSettingsDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initialConfig = DEFAULT_STYLE_CONFIG,
  initialGlobalConfig = DEFAULT_GLOBAL_CONFIG,
  maxHeadingLevel = 3, // 默认显示3级标题
}) => {
  const [config, setConfig] = useState<StyleConfig>(initialConfig);
  const [globalConfig, setGlobalConfig] = useState<GlobalConfig>(initialGlobalConfig);
  const [activeTab, setActiveTab] = useState<StyleTabKey>('heading1');
  const [activeCategory, setActiveCategory] = useState<'style' | 'global'>('style');
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfig(initialConfig);
      setGlobalConfig(initialGlobalConfig);
      setActiveTab('heading1');
      setActiveCategory('style');
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen, initialConfig, initialGlobalConfig]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(config, globalConfig);
    onClose();
  };

  const updateStyle = (
    level: StyleTabKey,
    field: string,
    value: string | number | boolean
  ) => {
    setConfig((prev) => ({
      ...prev,
      [level]: {
        ...(prev[level] as object),
        [field]: value,
      },
    }));
  };

  const applyPreset = (presetConfig: StyleConfig) => {
    setConfig(presetConfig);
  };

  // 根据 maxHeadingLevel 动态生成标签
  const tabs = [
    { key: 'heading1' as StyleTabKey, label: '一级标题' },
    ...(maxHeadingLevel >= 2 ? [{ key: 'heading2' as StyleTabKey, label: '二级标题' }] : []),
    ...(maxHeadingLevel >= 3 ? [{ key: 'heading3' as StyleTabKey, label: '三级标题' }] : []),
    ...(maxHeadingLevel >= 4 ? [{ key: 'heading4' as StyleTabKey, label: '四级标题' }] : []),
    ...(maxHeadingLevel >= 5 ? [{ key: 'heading5' as StyleTabKey, label: '五级标题' }] : []),
    ...(maxHeadingLevel >= 6 ? [{ key: 'heading6' as StyleTabKey, label: '六级标题' }] : []),
    { key: 'body' as StyleTabKey, label: '正文内容' },
  ];

  const renderStyleSettings = () => {
    const level = activeTab;
    const currentConfig = config[level];

    // 如果当前层级没有配置（如 heading4-6 可能不存在），使用默认值
    const safeConfig = currentConfig || {
      fontFamily: '宋体',
      fontSize: 14,
      color: '#333333',
      lineHeight: 1.5,
      pageBreak: false,
    };

    return (
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        {/* 字体 */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            字体系列
          </label>
          <select
            value={safeConfig.fontFamily}
            onChange={(e) => updateStyle(level, 'fontFamily', e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-400 cursor-pointer"
          >
            {FONT_OPTIONS.map((font) => (
              <option key={font.value} value={font.value}>
                {font.label}
              </option>
            ))}
          </select>
        </div>

        {/* 字号 */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            字号大小
          </label>
          <select
            value={safeConfig.fontSize}
            onChange={(e) => updateStyle(level, 'fontSize', parseInt(e.target.value))}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-400 cursor-pointer"
          >
            {FONT_SIZE_OPTIONS.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
        </div>

        {/* 颜色 */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            字体颜色
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={safeConfig.color}
              onChange={(e) => updateStyle(level, 'color', e.target.value)}
              className="w-10 h-8 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
            />
            <input
              type="text"
              value={safeConfig.color}
              onChange={(e) => updateStyle(level, 'color', e.target.value)}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono hover:border-gray-400"
              placeholder="#000000"
            />
          </div>
        </div>

        {/* 行距 */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            行距倍数
          </label>
          <select
            value={safeConfig.lineHeight}
            onChange={(e) => updateStyle(level, 'lineHeight', parseFloat(e.target.value))}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-400 cursor-pointer"
          >
            {LINE_HEIGHT_OPTIONS.map((height) => (
              <option key={height.value} value={height.value}>
                {height.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* 背景遮罩 - 带动画 */}
        <div
          className={`fixed inset-0 bg-gray-900 transition-all duration-300 ${
            isAnimating ? 'bg-opacity-60 backdrop-blur-sm' : 'bg-opacity-0'
          }`}
          onClick={onClose}
        ></div>

        {/* 对话框 - 带动画 */}
        <div
          className={`inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all duration-300 sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full ${
            isAnimating ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          {/* 头部 - 精简设计 */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm">
                <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-white">设置</h3>
                <p className="text-[10px] text-blue-100">对文件生成全局设置</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/90 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-all duration-200"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* 内容区域 - 带有一级分类导航 */}
          <div className="flex bg-white" style={{ minHeight: '400px' }}>
            {/* 一级分类导航 - 左侧 */}
            <div className="w-20 bg-gradient-to-b from-blue-50 to-blue-100 border-r border-blue-200 flex flex-col py-3">
              <button
                onClick={() => setActiveCategory('style')}
                className={`mx-2 mb-2 px-2 py-2.5 text-xs font-medium transition-all duration-200 rounded-lg flex flex-col items-center ${
                  activeCategory === 'style'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-white hover:shadow-sm'
                }`}
              >
                <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                <span>样式</span>
              </button>
              <button
                onClick={() => setActiveCategory('global')}
                className={`mx-2 px-2 py-2.5 text-xs font-medium transition-all duration-200 rounded-lg flex flex-col items-center ${
                  activeCategory === 'global'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-white hover:shadow-sm'
                }`}
              >
                <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>全局</span>
              </button>
            </div>

            {/* 右侧内容区 */}
            <div className="flex-1 flex flex-col">
              {activeCategory === 'style' ? (
                <>
                  {/* 样式设置内容 */}
                  <div className="flex flex-1">
                    {/* 二级标签栏 */}
                    <div className="w-32 bg-gradient-to-b from-gray-50 to-gray-100 border-r border-gray-200 flex flex-col py-2">
                      {tabs.map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() => setActiveTab(tab.key)}
                          className={`relative mx-1.5 mb-1.5 px-3 py-2 text-left text-xs font-medium transition-all duration-200 rounded-lg ${
                            activeTab === tab.key
                              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                              : 'text-gray-600 hover:bg-white hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center space-x-1.5">
                            <div className={`w-1 h-1 rounded-full ${
                              activeTab === tab.key ? 'bg-white' : 'bg-gray-400'
                            }`}></div>
                            <span>{tab.label}</span>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* 样式设置详情 */}
                    <div className="flex-1 flex flex-col">
                      {/* 快速预设 - 仅在样式分类下显示 */}
                      <div className="bg-gradient-to-b from-gray-50 to-white px-4 py-3 border-b border-gray-200">
                        <div className="flex items-center mb-2">
                          <SparklesIcon className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
                          <h4 className="text-xs font-semibold text-gray-700">快速预设</h4>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {Object.entries(PRESET_STYLES).map(([key, preset]) => {
                            const Icon = preset.icon;
                            return (
                              <button
                                key={key}
                                onClick={() => applyPreset(preset.config)}
                                className="flex items-center justify-center space-x-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 group"
                              >
                                <Icon className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                <span className="text-xs font-medium text-gray-700 group-hover:text-blue-600">
                                  {preset.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <div className="w-0.5 h-4 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full mr-2"></div>
                            <h3 className="text-sm font-bold text-gray-800">
                              {tabs.find(t => t.key === activeTab)?.label}
                            </h3>
                          </div>
                          
                          {activeTab !== 'body' && config[activeTab] && (
                            <div 
                              className="flex items-center space-x-1.5 group/checkbox"
                              title="勾选后，该标题与上一内容结尾分页（紧挨的多级标题不分页）"
                            >
                              <input
                                type="checkbox"
                                checked={'pageBreak' in config[activeTab]! ? (config[activeTab]!.pageBreak || false) : false}
                                onChange={(e) => updateStyle(activeTab, 'pageBreak', e.target.checked)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded cursor-pointer focus:ring-blue-500"
                              />
                              <span className="text-xs font-medium text-gray-700 group-hover/checkbox:text-blue-600 transition-colors cursor-pointer select-none">
                                章节分页
                              </span>
                            </div>
                          )}
                        </div>
                        {renderStyleSettings()}
                      </div>

                      {/* 预览区 */}
                      <div className="bg-gradient-to-b from-gray-50 to-gray-100 px-4 py-3 border-t border-gray-200">
                        <div className="flex items-center mb-2">
                          <svg className="w-3.5 h-3.5 mr-1.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <h3 className="text-xs font-bold text-gray-700">实时预览</h3>
                        </div>
                        <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                          <div
                            style={{
                              fontFamily: config.heading1.fontFamily,
                              fontSize: `${config.heading1.fontSize}px`,
                              color: config.heading1.color,
                              lineHeight: config.heading1.lineHeight,
                              fontWeight: 'bold',
                            }}
                            className="transition-all duration-200"
                          >
                            一级标题
                          </div>
                          <div
                            style={{
                              fontFamily: config.heading2.fontFamily,
                              fontSize: `${config.heading2.fontSize}px`,
                              color: config.heading2.color,
                              lineHeight: config.heading2.lineHeight,
                              fontWeight: 'bold',
                            }}
                            className="transition-all duration-200"
                          >
                            二级标题
                          </div>
                          <div
                            style={{
                              fontFamily: config.heading3.fontFamily,
                              fontSize: `${config.heading3.fontSize}px`,
                              color: config.heading3.color,
                              lineHeight: config.heading3.lineHeight,
                              fontWeight: 'bold',
                            }}
                            className="transition-all duration-200"
                          >
                            三级标题
                          </div>
                          <div
                            style={{
                              fontFamily: config.body.fontFamily,
                              fontSize: `${config.body.fontSize}px`,
                              color: config.body.color,
                              lineHeight: config.body.lineHeight,
                            }}
                            className="transition-all duration-200"
                          >
                            正文内容示例
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* 全局设置内容 */}
                  <div className="p-5">
                    <div className="flex items-center mb-4">
                      <div className="w-0.5 h-4 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full mr-2"></div>
                      <h3 className="text-sm font-bold text-gray-800">AI 生成方向</h3>
                    </div>
                    
                    <div className="space-y-3">
                      {/* 段落缩进选项 */}
                      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                        <div>
                          <label className="text-sm font-medium text-gray-700">段落开头缩进</label>
                          <p className="text-xs text-gray-400 mt-0.5">导出Word时正文段落首行缩进两个字符</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setConfig(prev => ({ ...prev, paragraphIndent: !(prev.paragraphIndent !== false) }))}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            config.paragraphIndent !== false ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              config.paragraphIndent !== false ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          全局偏向生成方向
                          <span className="text-gray-400 font-normal ml-1">（适用于所有章节）</span>
                        </label>
                        <textarea
                          value={globalConfig.aiDirection}
                          onChange={(e) => setGlobalConfig(prev => ({ ...prev, aiDirection: e.target.value }))}
                          placeholder="例如：强调技术创新、突出项目经验、注重成本控制..."
                          rows={4}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        />
                      </div>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start">
                          <svg className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="text-xs text-blue-700">
                            <p className="font-medium mb-1">优先级说明</p>
                            <p>如果章节资源关联弹框中也设置了生成方向，将以章节弹框中的设置优先。</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 底部按钮 - 精简设计 */}
          <div className="bg-white px-5 py-3 flex justify-end items-center border-t border-gray-200">
            <div className="flex space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-1.5 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-1.5 text-sm font-medium bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30"
              >
                保存设置
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleSettingsDialog;
